import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building2, 
  FolderOpen, 
  CheckCircle,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart,
  DollarSign,
  Calendar,
  TrendingUp,
  AlertCircle,
  Clock,
  Filter,
  RefreshCw,
  Eye,
  MapPin,
  Mail,
  FileText,
  Settings,
  Plus,
  UserPlus,
  FileBarChart,
  User as UserIcon
} from 'lucide-react';
import { Row, Col, Button, Card, Form, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { apiService } from '../services/api';
import './Dashboard.css';

const DashboardMain = () => {
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProjects: 0,
    totalCompanies: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalValue: 0,
    avgProjectValue: 0,
    completionRate: 0,
    totalReleases: 0,
    totalLoads: 0,
    totalRetailQuotes: 0,
    totalProjectSites: 0
  });
  const [projects, setProjects] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('30d');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');

  // Chart data
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [projectTrendData, setProjectTrendData] = useState([]);

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .dashboard-main-wrapper *,
      .dashboard-main-wrapper *::before,
      .dashboard-main-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Fetch dashboard data - OPTIMIZED VERSION
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch optimized dashboard data in parallel
      const [metricsResponse, chartsResponse, activityResponse, projectsResponse] =
        await Promise.all([
          apiService.getDashboardMetrics(),
          apiService.getDashboardCharts(),
          apiService.getRecentActivity(),
          apiService.getProjectSummary(1, 5, statusFilter, companyFilter)
        ]);

      // Process metrics data
      if (metricsResponse.success) {
        setStats(metricsResponse.data);
      }

      // Process charts data
      if (chartsResponse.success) {
        setProjectStatusData(chartsResponse.data.projectStatus);
        setRevenueData(chartsResponse.data.revenueTrend);
        setProjectTrendData(chartsResponse.data.projectTrend);
      }

      // Process activity data
      if (activityResponse.success) {
        setRecentActivity(activityResponse.data);
      }

      // Process projects data
      if (projectsResponse.success) {
        setProjects(projectsResponse.data.projects);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch filtered projects when filters change
  const fetchFilteredProjects = async () => {
    try {
      const response = await apiService.getProjectSummary(1, 5, statusFilter, companyFilter);
      if (response.success) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error fetching filtered projects:', error);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'Active': '#10b981',
      'In Progress': '#3b82f6',
      'Completed': '#059669',
      'Closed': '#6b7280',
      'Planning': '#f59e0b',
      'Unknown': '#9ca3af'
    };
    return colors[status] || '#9ca3af';
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'company') {
      setCompanyFilter(value);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Refetch projects when filters change
  useEffect(() => {
    if (statusFilter !== 'all' || companyFilter !== 'all') {
      fetchFilteredProjects();
    }
  }, [statusFilter, companyFilter]);

  // Refresh data
  const handleRefresh = () => {
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="dashboard-main-wrapper">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-main-wrapper">
        <Alert variant="danger" className="m-4">
          <AlertCircle className="me-2" />
          {error}
          <Button variant="outline-danger" size="sm" className="ms-3" onClick={handleRefresh}>
            <RefreshCw size={16} className="me-1" />
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="dashboard-main-wrapper">
      <div className="modern-dashboard">
        {/* Header */}
        <div className="dashboard-header-section mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="dashboard-title">Dashboard</h1>
              <p className="dashboard-subtitle">Welcome back! Here's what's happening today.</p>
            </div>
            <div className="dashboard-actions">
              <Button variant="outline-secondary" className="me-2" onClick={handleRefresh}>
                <RefreshCw size={16} className="me-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Total Users</p>
                    <h3 className="stat-value">{stats.totalUsers}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">{stats.totalCompanies}</span>
                      <span className="change-period">companies</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
                    <Users className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Total Projects</p>
                    <h3 className="stat-value">{stats.totalProjects}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">{stats.completionRate}%</span>
                      <span className="change-period">completion rate</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <FolderOpen className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Active Projects</p>
                    <h3 className="stat-value">{stats.activeProjects}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">{stats.completedProjects}</span>
                      <span className="change-period">completed</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                    <CheckCircle className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Total Companies</p>
                    <h3 className="stat-value">{stats.totalCompanies}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">${parseFloat(stats.totalValue).toLocaleString()}</span>
                      <span className="change-period">total value</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                    <Building2 className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Additional Metrics Row */}
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Total Releases</p>
                    <h3 className="stat-value">{stats.totalReleases}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">{stats.totalLoads}</span>
                      <span className="change-period">total loads</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                    <CheckCircle className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Retail Quotes</p>
                    <h3 className="stat-value">{stats.totalRetailQuotes}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">${parseFloat(stats.avgProjectValue).toLocaleString()}</span>
                      <span className="change-period">avg per project</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                    <BarChart3 className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">Project Sites</p>
                    <h3 className="stat-value">{stats.totalProjectSites}</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">Active</span>
                      <span className="change-period">locations</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                    <MapPin className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="modern-stat-card h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="stat-content">
                    <p className="stat-title">System Health</p>
                    <h3 className="stat-value">98%</h3>
                    <div className="stat-change">
                      <span className="trend-icon up">
                        <ArrowUpRight size={14} />
                      </span>
                      <span className="change-text">All Systems</span>
                      <span className="change-period">operational</span>
                    </div>
                  </div>
                  <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
                    <CheckCircle className="text-white" size={24} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col lg={8} className="mb-3">
            <Card className="modern-chart-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Revenue Overview</h5>
                  <p className="text-muted mb-0">Monthly revenue performance</p>
                </div>
                <div className="chart-actions">
                  <Form.Select size="sm" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="d-inline-block w-auto">
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                    <option value="90d">90 Days</option>
                  </Form.Select>
                </div>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
                    <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4} className="mb-3">
            <Card className="modern-chart-card">
              <Card.Header>
                <h5 className="mb-0">Project Status</h5>
                <p className="text-muted mb-0">Current project distribution</p>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPieChart>
                    <Pie
                      data={projectStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {projectStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <div className="status-legend mt-3">
                  <div className="row g-2">
                    {projectStatusData.map((item, index) => (
                      <div key={index} className="col-6 col-md-4">
                        <div className="d-flex align-items-center">
                          <div 
                            className="status-dot me-2" 
                            style={{ backgroundColor: item.color, width: '8px', height: '8px', borderRadius: '50%' }}
                          ></div>
                          <span className="small text-truncate">{item.name} ({item.value})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Project Trends */}
        

        {/* Filters and Project Summary */}
        

        {/* Recent Activity and Quick Actions */}
        <Row>
          <Col lg={6} className="mb-3">
            <Card className="modern-card">
              <Card.Header>
                <h5 className="mb-0">Recent Activity</h5>
              </Card.Header>
              <Card.Body>
                <div className="activity-list">
                  {recentActivity.map(activity => {
                    const Icon = activity.icon === 'FolderOpen' ? FolderOpen : 
                                 activity.icon === 'CheckCircle' ? CheckCircle :
                                 activity.icon === 'Truck' ? BarChart3 : Building2;
                    return (
                      <div key={activity.id} className="activity-item">
                        <div className={`activity-icon bg-${activity.color}`}>
                          <Icon size={16} className="text-white" />
                        </div>
                        <div className="activity-content">
                          <div className="activity-title">{activity.title}</div>
                          <div className="activity-desc">{activity.description}</div>
                          <div className="activity-time">{activity.time}</div>
                          {activity.action && (
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              className="mt-2"
                              onClick={() => {
                                if (activity.actionUrl) {
                                  window.location.href = activity.actionUrl;
                                }
                              }}
                            >
                              {activity.action}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6} className="mb-3">
            <Card className="modern-card">
              <Card.Header>
                <h5 className="mb-0">Quick Actions</h5>
                <p className="text-muted mb-0 small">Common tasks and shortcuts</p>
              </Card.Header>
              <Card.Body>
                <div className="quick-actions">
                  <Row className="g-2">
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-primary" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/users', { state: { openAddUserModal: true } })}
                      >
                        <UserPlus size={16} className="me-2" />
                        Add New User
                      </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-success" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/companies')}
                      >
                        <Building2 size={16} className="me-2" />
                        View Companies
                      </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-info" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/users')}
                      >
                        <Users size={16} className="me-2" />
                        Manage Users
                      </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-secondary" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/email')}
                      >
                        <Mail size={16} className="me-2" />
                        Email Center
                      </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-warning" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/project-reports')}
                      >
                        <FileBarChart size={16} className="me-2" />
                        Generate Report
                      </Button>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Button 
                        variant="outline-danger" 
                        className="w-100 quick-action-btn"
                        onClick={() => navigate('/profile')}
                      >
                        <UserIcon size={16} className="me-2" />
                        View Profile
                      </Button>
                    </Col>
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DashboardMain; 